/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import request from 'modules/common/utils/request';
import { llmActions } from './slice';
import API from './constants';

//
export function* createModelGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_LLM_MODEL, payload);
    yield put(llmActions.createModelSucceeded(response));
  } catch (error) {
    yield put(llmActions.createModelFailed(error?.message));
  }
}

export function* getAllModelsGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_LLM_MODELS, payload);
    yield put(llmActions.getAllModelsSucceeded(response));
  } catch (error) {
    yield put(llmActions.getAllModelsFailed(error?.message));
  }
}

/**
 * Get default model information generator function to obtain default model information
 * @param {*} param0
 */
export function* getDefaultModelsInformationGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_DEFAULT_MODELS_INFORMATION, payload);
    yield put(llmActions.getDefaultModelsInformationSucceeded(response));
  } catch (error) {
    yield put(llmActions.getDefaultModelsInformationFailed(error?.message));
  }
}
/**
 * Update system information generator function to update system information
 * @param {*} param0
 */
export function* updateDefaultModelsInformationGenerator({ payload }) {
  try {
    const response = yield call(request, API.PATCH_DEFAULT_MODELS_INFORMATION, payload);
    yield put(llmActions.updateDefaultModelsInformationSucceeded(response));
  } catch (error) {
    yield put(llmActions.updateDefaultModelsInformationFailed(error?.message));
  }
}
/**
 * Create system information generator function to create system information
 * @param {*} param0
 */
export function* createDefaultModelsInformationGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_DEFAULT_MODELS_INFORMATION, payload);
    yield put(llmActions.createDefaultModelsInformationSucceeded(response));
  } catch (error) {
    yield put(llmActions.createDefaultModelsInformationFailed(error?.message));
  }
}

export function* getModelUsageGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_LLM_MODEL_USAGE, payload);
    yield put(llmActions.getModelUsageSucceeded(response));
  } catch (error) {
    yield put(llmActions.getModelUsageFailed(error?.message));
  }
}

export function* getLlmLogsGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_LLM_LOGS, payload);
    yield put(llmActions.getLlmLogsSucceeded(response));
  } catch (error) {
    yield put(llmActions.getLlmLogsFailed(error?.message));
  }
}

/**
 * Fetch every page of logs in the requested period so the daily charts cover the
 * whole period rather than the activity table's current page
 * @param {*} param0 - payload.query must not contain `page`
 */
export function* getChartLogsGenerator({ payload }) {
  try {
    const { query, ...rest } = payload;
    let page = 1;
    let totalPages = 1;
    let results = [];
    do {
      const response = yield call(request, API.GET_LLM_LOGS, { ...rest, query: `${query}&page=${page}` });
      results = results.concat(response?.results ?? []);
      totalPages = response?.totalPages ?? 1;
      page += 1;
    } while (page <= totalPages);
    yield put(llmActions.getChartLogsSucceeded(results));
  } catch (error) {
    yield put(llmActions.getChartLogsFailed(error?.message));
  }
}

export function* llmSaga() {
  yield takeLatest(llmActions.createModel.type, createModelGenerator);
  yield takeLatest(llmActions.getAllModels.type, getAllModelsGenerator);
  yield takeLatest(llmActions.getDefaultModelsInformation.type, getDefaultModelsInformationGenerator);
  yield takeLatest(llmActions.updateDefaultModelsInformation.type, updateDefaultModelsInformationGenerator);
  yield takeLatest(llmActions.createDefaultModelsInformation.type, createDefaultModelsInformationGenerator);
  yield takeLatest(llmActions.getModelUsage.type, getModelUsageGenerator);
  yield takeLatest(llmActions.getLlmLogs.type, getLlmLogsGenerator);
  yield takeLatest(llmActions.getChartLogs.type, getChartLogsGenerator);}
//
export default llmSaga;
